/**
 * Phase 8 · Cluster C-42 — Agent tool invocation audit service.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.6
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.6
 *
 * One row per `toolRegistry.dispatch` call (see
 * `src/modules/agent/tools/registry.ts`). Writes are fire-and-forget from
 * the registry's `finally` block — a logging failure never blocks the tool
 * result from reaching the user. Reads power Settings → Observability in
 * a later PR.
 *
 * Privacy note:
 *   We hash `argsJson` rather than persisting it. Some Phase-8 tools (the
 *   connector ones in C-43/C-44) accept recruiter-sourced free text that
 *   may contain candidate PII; keeping a hash lets us dedupe / replay
 *   without storing that input. If the hashing primitive is unavailable
 *   (very old browsers) we fall back to a short tag — rows still land,
 *   just without cross-row dedup.
 */
import { supabase } from '../client';

export interface AgentToolInvocation {
  id: string;
  orgId: string;
  threadId: string;
  toolName: string;
  argsHash: string;
  resultSummary: string | null;
  durationMs: number;
  ok: boolean;
  error: string | null;
  createdAt: string;
  /**
   * Phase 8 · C-48 — audit columns from migration 109. `capability`
   * classifies the tool (`read` | `write`); `planId`/`stepIndex` link the
   * row to its parent plan (NULL for one-shot/auto dispatches); and
   * `approvalMode` distinguishes a manual click from an autonomous
   * execution. All nullable for rows written before migration 109 runs.
   */
  capability: 'read' | 'write' | null;
  planId: string | null;
  stepIndex: number | null;
  approvalMode: 'manual' | 'auto' | null;
}

export interface AgentToolInvocationLogInput {
  orgId: string;
  threadId: string;
  toolName: string;
  /** JSON-serialised args. We hash this before persisting. */
  argsJson: string;
  ok: boolean;
  resultSummary: string | null;
  error: string | null;
  durationMs: number;
  /** Phase 8 · C-48 — see AgentToolInvocation above. */
  capability?: 'read' | 'write' | null;
  planId?: string | null;
  stepIndex?: number | null;
  approvalMode?: 'manual' | 'auto' | null;
}

/**
 * Hex sha256 via Web Crypto (available in every browser we ship to and
 * in Node 20+). We swallow the failure path because a missing digest
 * primitive must not break the tool loop — the audit row is advisory.
 */
async function sha256Hex(input: string): Promise<string> {
  try {
    const subtle =
      (typeof globalThis !== 'undefined' ? globalThis.crypto?.subtle : undefined) ?? null;
    if (!subtle) return `nohash:${input.length}`;
    const bytes = new TextEncoder().encode(input);
    const buf = await subtle.digest('SHA-256', bytes);
    const view = new Uint8Array(buf);
    let out = '';
    for (let i = 0; i < view.length; i += 1) {
      out += view[i].toString(16).padStart(2, '0');
    }
    return out;
  } catch {
    return `nohash:${input.length}`;
  }
}

/** Truncate result summaries to fit the ≤ 500-char DB column. */
function clampSummary(raw: string | null): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed.length <= 500) return trimmed;
  return `${trimmed.slice(0, 497)}...`;
}

// One-shot warning flag so we don't spam the console on every tool call
// when the dev DB is still on the pre-112 schema.
let hasWarnedMigration112 = false;

export const agentToolInvocationsService = {
  /**
   * Append one invocation row. Returns void and never throws — callers use
   * `void agentToolInvocationsService.log(...)` from a finally block.
   * We still log warnings to the console so the audit path is debuggable
   * when a writer hits an RLS issue.
   */
  async log(row: AgentToolInvocationLogInput): Promise<void> {
    if (!row.orgId || !row.threadId || !row.toolName) return;
    try {
      const args_hash = await sha256Hex(row.argsJson ?? '');

      const baseCols = {
        org_id: row.orgId,
        thread_id: row.threadId,
        tool_name: row.toolName,
        args_hash,
        result_summary: clampSummary(row.resultSummary),
        duration_ms: Math.max(0, Math.round(row.durationMs)),
        ok: row.ok,
        error: row.error?.slice(0, 2000) ?? null,
      } as const;

      // Phase 8 · C-48 — new audit columns (migration 109). Tried on the
      // first attempt; if the column hasn't been applied on this database
      // yet we fall back to the base shape so the tool loop stays
      // observable instead of dropping every audit row.
      const withNewCols = {
        ...baseCols,
        capability: row.capability ?? null,
        plan_id: row.planId ?? null,
        step_index: row.stepIndex ?? null,
        approval_mode: row.approvalMode ?? null,
      };

      const first = await supabase
        .from('ggj_b2b_agent_tool_invocations')
        .insert(withNewCols);

      if (!first.error) return;

      const msg = first.error.message || '';
      const missingNewCol = /could not find the '.*' column|schema cache/i.test(msg);
      if (!missingNewCol) {
        console.warn('[agentToolInvocationsService.log] insert failed', msg);
        return;
      }

      // Pre-112 schema — retry without the new audit columns.
      const retry = await supabase
        .from('ggj_b2b_agent_tool_invocations')
        .insert(baseCols);
      if (retry.error) {
        console.warn(
          '[agentToolInvocationsService.log] insert failed (fallback)',
          retry.error.message,
        );
      } else if (!hasWarnedMigration112) {
        hasWarnedMigration112 = true;
        console.warn(
          '[agentToolInvocationsService.log] migration 109 not applied — plan/approval_mode columns will be NULL until the DB is migrated.',
        );
      }
    } catch (err) {
      console.warn('[agentToolInvocationsService.log] unexpected error', err);
    }
  },

  /**
   * Most-recent invocations for `orgId`, newest first. Powers the future
   * Settings → Observability panel and is handy for smoke testing that
   * the registry is wired correctly (see §8.6).
   */
  async recent(orgId: string, limit = 50): Promise<AgentToolInvocation[]> {
    if (!orgId) return [];
    const capped = Math.min(Math.max(1, limit), 200);

    const fullCols =
      'id, org_id, thread_id, tool_name, args_hash, result_summary, duration_ms, ok, error, created_at, capability, plan_id, step_index, approval_mode';
    const baseCols =
      'id, org_id, thread_id, tool_name, args_hash, result_summary, duration_ms, ok, error, created_at';

    let { data, error } = await supabase
      .from('ggj_b2b_agent_tool_invocations')
      .select(fullCols)
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(capped);

    if (error && /could not find the '.*' column|schema cache/i.test(error.message || '')) {
      // Pre-112 schema — retry without the new audit columns.
      const retry = await supabase
        .from('ggj_b2b_agent_tool_invocations')
        .select(baseCols)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
        .limit(capped);
      data = retry.data;
      error = retry.error;
    }

    if (error) {
      console.warn('[agentToolInvocationsService.recent] failed', error.message);
      return [];
    }

    type Row = {
      id: string;
      org_id: string;
      thread_id: string;
      tool_name: string;
      args_hash: string;
      result_summary: string | null;
      duration_ms: number;
      ok: boolean;
      error: string | null;
      created_at: string;
      capability: string | null;
      plan_id: string | null;
      step_index: number | null;
      approval_mode: string | null;
    };

    return ((data ?? []) as Row[]).map((r) => ({
      id: r.id,
      orgId: r.org_id,
      threadId: r.thread_id,
      toolName: r.tool_name,
      argsHash: r.args_hash,
      resultSummary: r.result_summary,
      durationMs: r.duration_ms,
      ok: r.ok,
      error: r.error,
      createdAt: r.created_at,
      capability: (r.capability === 'read' || r.capability === 'write') ? r.capability : null,
      planId: r.plan_id,
      stepIndex: r.step_index,
      approvalMode: (r.approval_mode === 'manual' || r.approval_mode === 'auto') ? r.approval_mode : null,
    }));
  },
};
